import { Box, Button, Divider, Group, Tabs } from "@mantine/core"
import { DownloadSimpleIcon, PlusIcon, TruckIcon } from "@phosphor-icons/react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { OrderApi, SplitApi } from "../../apis"
import { PageLayout } from "../../components/layout"
import { useSalesActions } from "../../components/order/order-actions"
import { usePermission } from "../../components/permission"
import { BulkActionBar, FilterChip, ReasonTooltip, SegmentedTabs } from "../../components/ui"
import { DisabledReason, HelpButtonGroup, HelpIcon, useHelpContext } from "@dehub/theme"
import type { ConsignmentFilter } from "./list/types"
import HttpHandler from "../../utils/http-handler"
import { message } from "../../utils/message"
import { SupplierSplitList, SupplierSplitListRef } from "../supplier-split/list"
import { SupplierSalesList, SupplierSalesListRef } from "./list"

export function SupplierSalesTabs() {
  const { t } = useTranslation(["supplierSales", "common"])
  const breadcrumbs = useBreadcrumb(t)
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState("DELIVERY")
  const [consignmentFilter, setConsignmentFilter] = useState<ConsignmentFilter>("ALL")
  const [checked, setChecked] = useState<string[]>([])
  const [actions, setActions] = useState<string[]>([])
  const [splitChecked, setSplitChecked] = useState<string[]>([])
  const [action, setAction] = useState<string[]>([])
  const [excelBusy, setExcelBusy] = useState<boolean>(false)
  const supplierSalesListRef = useRef<SupplierSalesListRef>(null)
  const supplierSplistListRef = useRef<SupplierSplitListRef>(null)

  const ORD_LIST = usePermission("ORD_LIST")
  const ORD_SPLIT = usePermission("ORD_SPLIT")
  const ORD_SPLIT_DN_ASSIGN = usePermission("ORD_SPLIT_DN_ASSIGN")
  const { permissionState } = useSalesActions()
  const createDeliveryPerm = permissionState("deliveryCreate")
  const newOrderPerm = permissionState("edit") // ORD_SAVE or ORD_SEND (BE order/create.ts)

  const isDelivery = activeTab === "DELIVERY"
  const selected = isDelivery ? checked : splitChecked

  // @dehub/help: the SO list is the SENDER side (D16: it only creates deliveries); {{count}} feeds the bulk toggletip.
  const { set: setHelpContext } = useHelpContext()
  useEffect(() => { setHelpContext({ side: "SENDER", vars: { count: selected.length } }) }, [selected.length, setHelpContext])

  const downloadExcel = async () => {
    setExcelBusy(true)
    try {
      if (isDelivery) {
        const { url } = await OrderApi.excel({
          filter: supplierSalesListRef.current?.getFilters(),
          order: supplierSalesListRef.current?.getOrder(),
        })
        window.open(url, "_blank")
      } else {
        const { url } = await SplitApi.excel({
          filter: supplierSplistListRef.current?.getFilters(),
          order: supplierSplistListRef.current?.getOrder(),
        })
        window.open(url, "_blank")
      }
    } catch (err) {
      message.error((err as HttpHandler)?.message!)
    } finally {
      setExcelBusy(false)
    }
  }

  // «Хүргэлт үүсгэх» / «Хуваарилах» act on the selected rows, so they live in the selection bar.
  const bulkPermReason = isDelivery ? createDeliveryPerm.reason : ORD_SPLIT_DN_ASSIGN.reason

  const excelReason = isDelivery ? ORD_LIST.reason : ORD_SPLIT.reason

  return (
    <PageLayout
      title={t("sales.title")}
      breadcrumb={breadcrumbs}
      extra={[
        <ReasonTooltip key="excel" reason={excelReason}>
          <Button variant="default" leftSection={<DownloadSimpleIcon size={16} />} loading={excelBusy} onClick={downloadExcel}>
            {t("sales.excelDownload")}
          </Button>
        </ReasonTooltip>,
        // Primary entry point: «?» explains what the form does (R2); missing permission → reason popover (R4).
        <DisabledReason key="new" helpKey="order.supplier-sales-list.reason.new" reason="PERM" when={!!newOrderPerm.reason} position="bottom-end">
          <HelpButtonGroup helpKey="order.supplier-sales-list.action.new">
            <Button data-help-id="new-order" leftSection={<PlusIcon size={16} />} onClick={() => navigate("/supplier-sales/new")}>
              {t("sales.newOrder")}
            </Button>
          </HelpButtonGroup>
        </DisabledReason>,
      ]}>
      {!ORD_LIST.hasAccess ? (
        <Box>{ORD_LIST.accessDenied()}</Box>
      ) : (
        <Box>
          {/* One header row for every list (concept shared with de-network-admin): segmented tabs · divider · type chips · «?». */}
          <Group gap="sm" wrap="wrap" mb="md">
            <SegmentedTabs
              value={activeTab}
              onChange={setActiveTab}
              data={[{ value: "DELIVERY", label: t("sales.allOrders") }, { value: "SPLIT", label: t("sales.partialDelivery") }]}
            />
            {isDelivery && (
              <>
                <Divider orientation="vertical" visibleFrom="sm" h={24} my="auto" />
                <Group gap="xs">
                  {(["ALL", "REGULAR", "CONSIGNMENT", "VMI_CRON"] as ConsignmentFilter[]).map((code) => (
                    <FilterChip key={code} active={consignmentFilter === code} onClick={() => setConsignmentFilter(code)}>
                      {t(`sales.consignmentFilters.${{ ALL: "all", REGULAR: "regular", CONSIGNMENT: "consignment", VMI_CRON: "vmiAuto" }[code]}`)}
                    </FilterChip>
                  ))}
                  {/* Нэр томьёоны «?» — бүлэгт нэг (R2, @dehub/help) */}
                  <HelpIcon helpKey="order.supplier-sales-list.types.order-type" />
                </Group>
              </>
            )}
          </Group>
        <Tabs value={activeTab} keepMounted={false}>
          <Tabs.Panel value="DELIVERY">
            <SupplierSalesList
              consignmentFilter={consignmentFilter}
              setConsignmentFilter={setConsignmentFilter}
              setChecked={setChecked}
              actions={actions}
              checked={checked}
              setActions={setActions}
              onCreate={() => navigate("/supplier-sales/new")}
              ref={supplierSalesListRef}
            />
          </Tabs.Panel>
          <Tabs.Panel value="SPLIT">
            <SupplierSplitList
              setSplitChecked={setSplitChecked}
              action={action}
              splitChecked={splitChecked}
              setAction={setAction}
              success={() => setActiveTab("SPLIT")}
              ref={supplierSplistListRef}
            />
          </Tabs.Panel>
        </Tabs>
        </Box>
      )}

      <BulkActionBar
        selectedCount={selected.length}
        onClear={() => (isDelivery ? setChecked([]) : setSplitChecked([]))}
        actions={[{
          key: "bulk",
          label: t(isDelivery ? "sales.createDelivery" : "sales.distribute"),
          icon: <TruckIcon size={16} />,
          primary: true,
          reason: bulkPermReason,
          onClick: () => (isDelivery ? setActions(["delivery", checked as never]) : setAction(["split", splitChecked as never])),
          helpKey: isDelivery ? "order.supplier-sales-list.action.create-delivery" : "order.supplier-sales-list.action.distribute",
          reasonHelpKey: isDelivery ? "order.supplier-sales-list.reason.create-delivery" : "order.supplier-sales-list.reason.distribute",
          reasonCode: bulkPermReason ? "PERM" : null,
          helpId: isDelivery ? "bulk-create-delivery" : "bulk-distribute",
        }]}
      />
    </PageLayout>
  )
}

const useBreadcrumb = (t: (key: string) => string) => [
  { to: "/", label: t("common:breadcrumb.home") },
  { label: t("sales.breadcrumb") },
]
